import { useState, useEffect, useRef } from 'react';
import {
  NetworkMode,
  NETWORKS,
  getActiveNetwork,
  setActiveNetwork,
  getNetworkConfig,
} from '../config/contracts';
import { isTyJoltOnline } from '../services/tyjoltSigner';

const NETWORK_ICONS: Record<NetworkMode, string> = {
  testnet: 'T',
  'tyjolt-local': 'Z',
  'tyjolt-testnet': 'Z',
  mainnet: 'M',
};

const NETWORK_COLORS: Record<NetworkMode, string> = {
  testnet: '#4FC3F7',
  'tyjolt-local': '#7C4DFF',
  'tyjolt-testnet': '#B388FF',
  mainnet: '#66BB6A',
};

export default function NetworkSwitcher() {
  const [network, setNetwork] = useState<NetworkMode>(getActiveNetwork());
  const [open, setOpen] = useState(false);
  const [tyjoltOnline, setTyjoltOnline] = useState<boolean | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (network.startsWith('tyjolt')) {
      isTyJoltOnline().then(setTyjoltOnline);
    }
  }, [network]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function switchNetwork(n: NetworkMode) {
    setActiveNetwork(n);
    setNetwork(n);
    setOpen(false);
    // Reload to re-initialize wallet connections
    window.location.reload();
  }

  const config = NETWORKS[network];
  const color = NETWORK_COLORS[network];

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          background: 'rgba(255,255,255,0.08)',
          border: `1px solid ${color}`,
          borderRadius: 8,
          padding: '4px 12px',
          color: color,
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: network.startsWith('tyjolt')
            ? (tyjoltOnline ? '#66BB6A' : '#EF5350')
            : color,
        }} />
        {config.name}
        <span style={{ fontSize: 10, opacity: 0.6 }}>
          {config.walletMode === 'dev-keypair' ? 'DEV' : ''}
        </span>
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: 4,
          background: '#1a1f36',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 8,
          padding: 4,
          zIndex: 9999,
          minWidth: 180,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}>
          {(Object.entries(NETWORKS) as [NetworkMode, typeof NETWORKS[NetworkMode]][]).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => switchNetwork(key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '8px 12px',
                background: key === network ? 'rgba(255,255,255,0.08)' : 'transparent',
                border: 'none',
                borderRadius: 6,
                color: '#fff',
                fontSize: 13,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span style={{
                width: 24, height: 24, borderRadius: 6,
                background: NETWORK_COLORS[key],
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700, color: '#fff',
              }}>
                {NETWORK_ICONS[key]}
              </span>
              <div>
                <div style={{ fontWeight: 600 }}>{cfg.name}</div>
                <div style={{ fontSize: 10, opacity: 0.5 }}>
                  {cfg.chainType === 'tyjolt' ? 'ZK-TVM Appchain' : 'TON Network'}
                  {cfg.walletMode === 'dev-keypair' ? ' (Dev)' : ''}
                </div>
              </div>
              {key === network && (
                <span style={{ marginLeft: 'auto', color: NETWORK_COLORS[key] }}>
                  &#10003;
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
